import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical, MessageCircle, Clock, CheckCircle2 } from 'lucide-react';
import { LanguageBadge } from '../LanguageBadge';
import EmployeeAvatar from './EmployeeAvatar';
import { maskPhone, formatCurrency } from '../../utils/format';

const ROLE_LABELS = {
  WAITER: 'Waiter',
  CHEF: 'Chef',
  SOUS_CHEF: 'Sous chef',
  KITCHEN_HELPER: 'Kitchen helper',
  CAPTAIN: 'Captain',
  STEWARD: 'Steward',
  CASHIER: 'Cashier',
  CLEANER: 'Cleaner',
  DISHWASHER: 'Dishwasher',
  MANAGER: 'Manager',
  DELIVERY: 'Delivery',
  OTHER: 'Other',
};

const MENU_WIDTH = 192;
const MENU_HEIGHT_ESTIMATE = 130;

function ActionsMenu({ employee, onEdit, onToggleActive }) {
  const { t } = useTranslation('team');
  const [anchorRect, setAnchorRect] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const open = !!anchorRect;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setAnchorRect(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  let top = 0;
  let left = 0;
  if (anchorRect) {
    top = anchorRect.bottom + 4;
    if (top + MENU_HEIGHT_ESTIMATE > window.innerHeight) {
      top = anchorRect.top - MENU_HEIGHT_ESTIMATE - 4;
    }
    left = anchorRect.right - MENU_WIDTH;
    if (left < 8) left = 8;
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          setAnchorRect(prev => prev ? null : rect);
        }}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        aria-label="Actions"
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          ref={menuRef}
          style={{ top, left, width: MENU_WIDTH }}
          className="fixed z-20 rounded-xl bg-white border border-gray-200 shadow-lg py-1"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setAnchorRect(null); onEdit(); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
          >
            {t('rowActions.edit')}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setAnchorRect(null); onToggleActive(); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
          >
            {employee.isActive ? t('rowActions.deactivate') : t('rowActions.reactivate')}
          </button>
          <button
            disabled
            title="Available after WhatsApp is enabled"
            className="w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
          >
            Send test message
          </button>
        </div>
      )}
    </div>
  );
}

function WhatsappColumn({ employee, onInvite }) {
  const { t } = useTranslation('team');

  if (!employee.isActive) {
    return <span className="text-sm text-gray-300">—</span>;
  }

  const status = employee.whatsappOptinStatus ?? 'NOT_INVITED';

  if (status === 'OPTED_IN') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 border border-green-200">
        <CheckCircle2 size={12} />
        {t('whatsapp.optedIn')}
      </span>
    );
  }

  if (status === 'PENDING') {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
          <Clock size={12} />
          {t('whatsapp.pending')}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onInvite(employee); }}
          className="text-xs font-medium text-orange-600 hover:text-orange-700 underline"
        >
          {t('whatsapp.resend')}
        </button>
      </div>
    );
  }

  if (status === 'DECLINED') {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500 border border-gray-200">
          {t('whatsapp.declined')}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onInvite(employee); }}
          className="text-xs font-medium text-orange-600 hover:text-orange-700 underline"
        >
          {t('whatsapp.reinvite')}
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onInvite(employee); }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 px-2.5 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-50 transition"
    >
      <MessageCircle size={13} />
      {t('whatsapp.invite')}
    </button>
  );
}

function EmployeeRow({ employee, onEdit, onToggleActive, onInvite }) {
  return (
    <tr onClick={onEdit} className="hover:bg-gray-50 cursor-pointer transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <EmployeeAvatar fullName={employee.fullName} />
          <span className="text-sm font-medium text-gray-900">{employee.fullName}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 font-mono whitespace-nowrap">
        {employee.employeeCode || <span className="text-gray-300">—</span>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {ROLE_LABELS[employee.jobRole] ?? employee.jobRole ?? '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 font-mono">
        {employee.phone ? maskPhone(employee.phone) : '—'}
      </td>
      <td className="px-4 py-3">
        <LanguageBadge code={employee.whatsappLanguage} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 tabular-nums">
        {employee.hourlyRateCost != null ? formatCurrency(employee.hourlyRateCost / 100) : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {employee.defaultShiftTemplateLabel
          ? employee.defaultShiftTemplateLabel
          : <span className="text-gray-300">—</span>}
      </td>
      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
        <WhatsappColumn employee={employee} onInvite={onInvite} />
      </td>
      <td className="px-4 py-3">
        {employee.isActive ? (
          <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 border border-green-200">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500 border border-gray-200">
            Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
        <ActionsMenu employee={employee} onEdit={onEdit} onToggleActive={onToggleActive} />
      </td>
    </tr>
  );
}

export default EmployeeRow;